import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_CONST } from "../../../const";
import {
  Table,
  Button,
  Input,
  Select,
  Tag,
  Dropdown,
  Modal,
  message,
  Empty,
  Flex,
  Upload,
} from "antd";
import {
  SearchOutlined,
  MoreOutlined,
  EyeOutlined,
  DownloadOutlined,
  FilterOutlined,
  UploadOutlined,
  InboxOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  FinanceContainer,
  PageHeader,
  MetricsSection,
  MetricCard,
  TableContainer,
  EmptyState,
} from "./Finance.style";

const { Dragger } = Upload;

const { Option } = Select;

const PartnerFinance = () => {
  const [searchText, setSearchText] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterStatus, setFilterStatus] = useState("");
  const [createInvoiceModalVisible, setCreateInvoiceModalVisible] = useState(false);
  const [selectedInvoiceRecord, setSelectedInvoiceRecord] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [uploadedInvoiceFile, setUploadedInvoiceFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Partner invoices from API
  const [receivables, setReceivables] = useState([]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }
      if (filterYear) body.year = parseInt(filterYear);
      if (filterStatus) body.partnerStatus = filterStatus.toLowerCase();
      
      const response = await axios.post(API_CONST.GET_INVOICES, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.invoices) {
        const transformed = response.data.invoices.map(inv => ({
          id: inv.id,
          recordId: inv.id,
          partnerInvoiceId: inv.partnerInvoiceNumber,
          talentId: inv.talentId,
          talentName: inv.talentName || 'Unknown',
          role: inv.jobRole || 'Unknown',
          month: inv.monthName,
          year: String(inv.year),
          workingDays: inv.billableHours ? Math.round(inv.billableHours / 8) : 0,
          billableHours: inv.billableHours,
          amount: parseFloat(inv.partnerSubtotal) || 0,
          gst: parseFloat(inv.partnerTaxAmount) || 0,
          totalAmount: parseFloat(inv.partnerTotalAmount) || 0,
          timesheetStatus: "Approved",
          invoiceStatus: inv.partnerStatus ? inv.partnerStatus.charAt(0).toUpperCase() + inv.partnerStatus.slice(1) : 'Pending',
          invoiceNumber: inv.partnerInvoiceNumber,
          dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString().split('T')[0] : '',
          partnerNotes: inv.partnerNotes || '',
        }));
        setReceivables(transformed);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterYear, filterStatus]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("authToken");
      const now = new Date();
      await axios.post(API_CONST.SYNC_NOW, {
        month: now.getMonth() + 1,
        year: now.getFullYear()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success("Sync completed successfully");
      fetchInvoices();
    } catch (error) {
      console.error("Error syncing:", error);
      message.error(error.response?.data?.message || "Failed to sync");
    } finally {
      setSyncing(false);
    }
  };

  const getFilteredReceivables = () => {
    let filtered = [...receivables];

    if (searchText) {
      filtered = filtered.filter(
        (r) =>
          r.talentName.toLowerCase().includes(searchText.toLowerCase()) ||
          r.talentId.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filterMonth) filtered = filtered.filter((r) => r.month === filterMonth);
    if (filterYear) filtered = filtered.filter((r) => r.year === filterYear);
    if (filterStatus) filtered = filtered.filter((r) => r.invoiceStatus === filterStatus);

    return filtered;
  };

  const getMetrics = () => {
    const totalAmount = receivables
      .filter((r) => r.invoiceStatus === "Paid")
      .reduce((sum, r) => sum + r.totalAmount, 0);
    const pending = receivables
      .filter((r) => !["Paid", "Overdue", "Cancelled", "Rejected"].includes(r.invoiceStatus))
      .reduce((sum, r) => sum + r.totalAmount, 0);
    const overdue = receivables
      .filter((r) => r.invoiceStatus === "Overdue")
      .reduce((sum, r) => sum + r.totalAmount, 0);
    const totalInvoices = receivables.length;

    return { totalAmount, pending, overdue, totalInvoices };
  };

  const metrics = getMetrics();

  const handleViewBreakup = (record) => {
    Modal.info({
      title: `Invoice Breakup - ${record.talentName}`,
      width: 600,
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      content: (
        <div style={{ marginTop: 16 }}>
          <div style={{ padding: "12px 16px", background: "#f8f9fd", borderRadius: 6, marginBottom: 16 }}>
            <div><strong>Talent ID:</strong> {record.talentId}</div>
            <div><strong>Talent Name:</strong> {record.talentName}</div>
            <div><strong>Role:</strong> {record.role}</div>
            <div><strong>Month:</strong> {record.month} {record.year}</div>
          </div>
          <div style={{ padding: 16, background: "#fff", borderRadius: 6, border: "1px solid #e8e8e8" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span>Working Days:</span>
              <strong>{record.workingDays} days</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span>Billable Hours:</span>
              <strong>{record.billableHours} hrs</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, paddingTop: 8, borderTop: "1px solid #f0f0f0" }}>
              <span>Base Amount:</span>
              <strong>₹ {record.amount.toLocaleString("en-IN")}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span>GST (18%):</span>
              <strong>₹ {record.gst.toLocaleString("en-IN")}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "2px solid #014c75" }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: "#014c75" }}>Total Amount:</span>
              <strong style={{ fontSize: 18, color: "#00d9a9" }}>₹ {record.totalAmount.toLocaleString("en-IN")}</strong>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleDownloadTimesheet = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_TIMESHEET_DOWNLOAD_URL,
        { invoiceId: record.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        message.warning("Timesheet file not available");
      }
    } catch (error) {
      console.error("Error downloading timesheet:", error);
      message.error(error.response?.data?.message || "Failed to download timesheet");
    }
  };

  const handleCreateInvoice = (record) => {
    setSelectedInvoiceRecord(record);
    setCreateInvoiceModalVisible(true);
    setInvoiceNumber(record.partnerInvoiceId || "");
    setInvoiceNotes(record.partnerNotes || "");
  };

  const handleCloseInvoiceModal = () => {
    setCreateInvoiceModalVisible(false);
    setSelectedInvoiceRecord(null);
    setInvoiceNumber("");
    setInvoiceNotes("");
    setUploadedInvoiceFile(null);
  };

  const handleSubmitInvoice = async () => {
    if (!uploadedInvoiceFile) {
      message.warning("Please upload an invoice file");
      return;
    }
    if (!invoiceNumber.trim()) {
      message.warning("Please enter an invoice number");
      return;
    }

    setUploadingInvoice(true);
    try {
      const token = localStorage.getItem("authToken");
      
      // Step 1: Get signed upload URL
      const urlResponse = await axios.post(
        `${API_CONST.INVOICE_UPLOAD_REQUEST_URL}/${selectedInvoiceRecord.id}`,
        { fileName: uploadedInvoiceFile.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { uploadUrl, key } = urlResponse.data;
      
      // Step 2: Upload file directly to signed URL
      await fetch(uploadUrl, {
        method: "PUT",
        body: uploadedInvoiceFile,
        headers: { "Content-Type": uploadedInvoiceFile.type || "application/pdf" },
      });
      
      // Step 3: Submit partner invoice with file path, invoice number and notes
      await axios.post(
        `${API_CONST.SUBMIT_PARTNER_INVOICE}/${selectedInvoiceRecord.id}`,
        {
          partnerInvoiceFileUrl: key,
          partnerInvoiceNumber: invoiceNumber,
          partnerNotes: invoiceNotes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updated = receivables.map((r) =>
        r.id === selectedInvoiceRecord.id 
          ? { ...r, invoiceStatus: "Submitted", partnerInvoiceId: invoiceNumber } 
          : r
      );
      setReceivables(updated);
      
      message.success(`Invoice ${invoiceNumber} uploaded and submitted successfully`);
      handleCloseInvoiceModal();
    } catch (error) {
      console.error("Error submitting invoice:", error);
      message.error(error.response?.data?.message || "Failed to submit invoice");
    } finally {
      setUploadingInvoice(false);
    }
  };

  const receivableColumns = [
    {
      title: "Record ID",
      dataIndex: "recordId",
      key: "recordId",
      width: 110,
      ellipsis: true,
      render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{text?.substring(0, 12)}...</span>,
    },
    {
      title: "Partner Invoice",
      dataIndex: "partnerInvoiceId",
      key: "partnerInvoiceId",
      width: 150,
      ellipsis: true,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1890ff', fontWeight: 500 }}>{text || '-'}</span>,
    },
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 150,
      ellipsis: true,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 130,
      ellipsis: true,
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 85,
      render: (text, record) => `${text?.substring(0, 3)} '${record.year?.toString().substring(2)}`,
    },
    {
      title: "Days",
      dataIndex: "workingDays",
      key: "workingDays",
      width: 70,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Hrs",
      dataIndex: "billableHours",
      key: "billableHours",
      width: 75,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: /** @type {'right'} */ ("right"),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#595959" }}>
          ₹ {(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "GST (18%)",
      dataIndex: "gst",
      key: "gst",
      width: 110,
      align: /** @type {'right'} */ ("right"),
      render: (gst) => (
        <span style={{ fontWeight: 500, color: "#8c8c8c" }}>
          ₹ {(gst || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 140,
      align: /** @type {'right'} */ ("right"),
      render: (total) => (
        <strong style={{ color: "#014c75", fontSize: 14 }}>
          ₹ {(total || 0).toLocaleString("en-IN")}
        </strong>
      ),
    },
    {
      title: "Timesheet",
      dataIndex: "timesheetStatus",
      key: "timesheetStatus",
      width: 110,
      align: /** @type {'center'} */ ("center"),
      render: (status) => (
        <Tag color={status === "Approved" ? "green" : status === "Verified" ? "blue" : "orange"}>{status}</Tag>
      ),
    },
    {
      title: "Invoice",
      dataIndex: "invoiceStatus",
      key: "invoiceStatus",
      width: 100,
      render: (status) => (
        <Tag
          color={
            status === "Paid" ? "green"
            : status === "Approved" ? "cyan"
            : status === "Verified" ? "blue"
            : status === "Submitted" ? "purple"
            : status === "Overdue" || status === "Rejected" ? "red"
            : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Notes",
      dataIndex: "partnerNotes",
      key: "partnerNotes",
      width: 140,
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: /** @type {'right'} */ ('right'),
      render: (_, record) => {
        const items = [];

        if (record.invoiceStatus === "Pending") {
          items.push({
            key: "uploadInvoice",
            label: "Upload Invoice",
            icon: <UploadOutlined />,
            onClick: () => handleCreateInvoice(record),
          });
        }

        if (record.invoiceStatus === "Submitted") {
          items.push({
            key: "editInvoice",
            label: "Edit Invoice",
            icon: <UploadOutlined />,
            onClick: () => handleCreateInvoice(record),
          });
          items.push({
            key: "downloadInvoice",
            label: "Download Invoice",
            icon: <DownloadOutlined />,
            onClick: () => handleDownloadInvoice(record),
          });
        }

        items.push(
          {
            key: "viewBreakup",
            label: "View Breakup",
            icon: <EyeOutlined />,
            onClick: () => handleViewBreakup(record),
          },
          {
            key: "downloadTimesheet",
            label: "Download Timesheet",
            icon: <DownloadOutlined />,
            onClick: () => handleDownloadTimesheet(record),
          }
        );

        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
          >
            <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
          </Dropdown>
        );
      },
    },
  ];

  const handleDownloadInvoice = async (record) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.INVOICE_DOWNLOAD_URL}/${record.id}?invoiceType=partner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else {
        message.warning("Invoice file not available");
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      message.error(error.response?.data?.message || "Failed to download invoice");
    }
  };

  return (
    <FinanceContainer>
      <PageHeader>
        <h1>Invoice</h1>
        <p>View billing records and invoices for your talents</p>
      </PageHeader>

      <MetricsSection>
        <MetricCard>
          <div className="metric-label">Total Amount Received</div>
          <div className="metric-value">
            ₹ {(metrics.totalAmount / 100000).toFixed(2)}L
          </div>
        </MetricCard>
        <MetricCard>
          <div className="metric-label">Current Receivable</div>
          <div className="metric-value" style={{ color: "#ffa500" }}>
            ₹ {(metrics.pending / 100000).toFixed(2)}L
          </div>
        </MetricCard>
        <MetricCard>
          <div className="metric-label">Overdue Amount</div>
          <div className="metric-value" style={{ color: "#ff4d4f" }}>
            ₹ {(metrics.overdue / 100000).toFixed(2)}L
          </div>
        </MetricCard>
        <MetricCard>
          <div className="metric-label">Total Invoices</div>
          <div className="metric-value">{metrics.totalInvoices}</div>
        </MetricCard>
      </MetricsSection>

      <Flex align="start" justify="space-between" style={{ marginBottom: "20px" }}>
        <Flex gap="middle">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name or ID..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Month
              </span>
            }
            value={filterMonth || undefined}
            onChange={setFilterMonth}
            allowClear
            style={{ width: 200 }}
          >
            <Option value="January">January</Option>
            <Option value="February">February</Option>
            <Option value="March">March</Option>
            <Option value="April">April</Option>
            <Option value="May">May</Option>
            <Option value="June">June</Option>
            <Option value="July">July</Option>
            <Option value="August">August</Option>
            <Option value="September">September</Option>
            <Option value="October">October</Option>
            <Option value="November">November</Option>
            <Option value="December">December</Option>
          </Select>
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Year
              </span>
            }
            value={filterYear || undefined}
            onChange={setFilterYear}
            allowClear
            style={{ width: 150 }}
          >
            <Option value="2024">2024</Option>
            <Option value="2023">2023</Option>
            <Option value="2022">2022</Option>
          </Select>
          <Select
            placeholder={
              <span>
                <FilterOutlined style={{ marginRight: 8 }} />
                Filter by Status
              </span>
            }
            value={filterStatus || undefined}
            onChange={setFilterStatus}
            allowClear
            style={{ width: 180 }}
          >
            <Option value="Pending">Pending</Option>
            <Option value="Sent">Sent</Option>
            <Option value="Paid">Paid</Option>
            <Option value="Overdue">Overdue</Option>
          </Select>
        </Flex>
        <Button
          type="link"
          icon={<SyncOutlined spin={syncing} />}
          onClick={handleSyncNow}
          loading={syncing}
          style={{ padding: 0, color: '#1890ff' }}
        >
          Sync Now
        </Button>
      </Flex>

      <TableContainer>
        {getFilteredReceivables().length > 0 ? (
          <Table
            columns={receivableColumns}
            dataSource={getFilteredReceivables()}
            rowKey="id"
            pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
              pageSize: 10,
              showTotal: (total) => `Total ${total} records`,
            }}
            scroll={{ x: 1400 }}
          />
        ) : (
          <EmptyState>
            <Empty description="No billing records found" />
          </EmptyState>
        )}
      </TableContainer>

      {/* Create Invoice Modal */}
      <Modal
        title={`${selectedInvoiceRecord?.invoiceStatus === 'Submitted' ? 'Update' : 'Create'} Invoice - ${selectedInvoiceRecord?.talentName || ''}`}
        open={createInvoiceModalVisible}
        onOk={handleSubmitInvoice}
        onCancel={handleCloseInvoiceModal}
        width={700}
        okText={uploadingInvoice ? "Uploading..." : (selectedInvoiceRecord?.invoiceStatus === 'Submitted' ? "Update Invoice" : "Create & Send Invoice")}
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" }, loading: uploadingInvoice, disabled: uploadingInvoice }}
      >
        {selectedInvoiceRecord && (
          <div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}>
            {/* Talent Details */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Talent Details
              </label>
              <div style={{ padding: "12px 16px", background: "#f8f9fd", borderRadius: 6 }}>
                <div><strong>Talent ID:</strong> {selectedInvoiceRecord.talentId}</div>
                <div><strong>Talent Name:</strong> {selectedInvoiceRecord.talentName}</div>
                <div><strong>Role:</strong> {selectedInvoiceRecord.role}</div>
                <div><strong>Month:</strong> {selectedInvoiceRecord.month} {selectedInvoiceRecord.year}</div>
                <div><strong>Working Days:</strong> {selectedInvoiceRecord.workingDays} days</div>
                <div><strong>Billable Hours:</strong> {selectedInvoiceRecord.billableHours} hours</div>
              </div>
            </div>

            {/* Invoice Number */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Invoice Number <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                placeholder="Enter invoice number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>

            {/* Amount Breakup */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Amount Breakup
              </label>
              <div style={{ padding: 16, background: "#fff5e6", borderRadius: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span>Base Amount ({selectedInvoiceRecord.workingDays} days):</span>
                  <strong>₹ {selectedInvoiceRecord.amount.toLocaleString("en-IN")}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span>GST (18%):</span>
                  <strong>₹ {selectedInvoiceRecord.gst.toLocaleString("en-IN")}</strong>
                </div>
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  paddingTop: 8, 
                  borderTop: "2px solid #014c75",
                  marginTop: 8
                }}>
                  <span style={{ fontSize: 16, fontWeight: 600, color: "#014c75" }}>Total Amount:</span>
                  <strong style={{ fontSize: 18, color: "#00d9a9" }}>
                    ₹ {selectedInvoiceRecord.totalAmount.toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>
            </div>

            {/* Notes/Comments */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Notes / Comments
              </label>
              <Input.TextArea
                placeholder="Enter any notes or comments for this invoice"
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                rows={3}
                style={{ width: "100%" }}
              />
            </div>

            {/* Invoice Upload */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Upload Invoice <span style={{ color: "red" }}>*</span>
              </label>
              <Dragger
                name="file"
                multiple={false}
                accept=".pdf,.doc,.docx"
                beforeUpload={(file) => {
                  const isPDF = file.type === "application/pdf" ||
                               file.type === "application/msword" ||
                               file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
                  if (!isPDF) {
                    message.error("You can only upload PDF or Word documents!");
                    return Upload.LIST_IGNORE;
                  }
                  setUploadedInvoiceFile(file);
                  return false;
                }}
                onRemove={() => {
                  setUploadedInvoiceFile(null);
                }}
                fileList={uploadedInvoiceFile ? [uploadedInvoiceFile] : []}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: "#00d9a9" }} />
                </p>
                <p className="ant-upload-text">Click or drag invoice file to this area to upload</p>
                <p className="ant-upload-hint">
                  Support for PDF, DOC, or DOCX files. Upload the signed invoice document.
                </p>
              </Dragger>
            </div>
          </div>
        )}
      </Modal>
    </FinanceContainer>
  );
};

export default PartnerFinance;
